import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Card,
    Button,
    Space,
    Input,
    Select,
    message,
    Typography,
    Row,
    Col,
    Statistic
} from 'antd';
import { SearchOutlined, DownloadOutlined, ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import { useMemberService } from '../../services/members/MemberServiceProvider';
import { MemberProfile } from '../../model/members/MemberProfile';
import { useLoading } from '../../common/loading/LoadingContext';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, GridApi, RowSelectionOptions } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Extend dayjs with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

interface MemberReportingProps {}

const MemberReporting: React.FC<MemberReportingProps> = () => {
    const memberService = useMemberService();
    const { withLoading } = useLoading();
    const navigate = useNavigate();
    const [members, setMembers] = useState<MemberProfile[]>([]);
    const [filteredMembers, setFilteredMembers] = useState<MemberProfile[]>([]);
    const [selectedCount, setSelectedCount] = useState<number>(0);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    
    // Date of birth filter states
    const [startMonth, setStartMonth] = useState<number | null>(null);
    const [startDay, setStartDay] = useState<number | null>(null);
    const [endMonth, setEndMonth] = useState<number | null>(null);
    const [endDay, setEndDay] = useState<number | null>(null);

    // Helper function to filter active members
    const filterActiveMembers = useCallback((membersToFilter: MemberProfile[]) => {
        const currentDate = dayjs();
        return membersToFilter.filter(member => {
            // Check if member is enabled
            if (!member.isEnabled) {
                return false;
            }
            
            // Check if current date is within membership validity period
            const validFrom = dayjs(member.validFrom);
            const validTo = dayjs(member.validTo);
            
            if (!validFrom.isValid() || !validTo.isValid()) {
                return false;
            }
            
            // Check if current date is on or after validFrom and on or before validTo
            return currentDate.isSameOrAfter(validFrom) && currentDate.isSameOrBefore(validTo);
        });
    }, []);

    const fetchMembers = useCallback(async () => {
        try {
            const memberProfiles = await withLoading(memberService.getMemberProfiles());
            setMembers(memberProfiles);
            
            // Filter for active members only
            const activeMembers = filterActiveMembers(memberProfiles);
            setFilteredMembers(activeMembers);
        } catch (error) {
            console.error('Error fetching members:', error);
            message.error('Failed to fetch members');
        }
    }, [memberService, filterActiveMembers, withLoading]);

    // Fetch members on component mount
    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    // Clear selection when filtered members change
    useEffect(() => {
        if (gridApi) {
            gridApi.deselectAll();
            setSelectedCount(0);
        }
    }, [filteredMembers, gridApi]);

    // Filter members by search text (only active members)
    const handleSearch = useCallback((value: string) => {
        // First filter for active members
        const activeMembers = filterActiveMembers(members);
        
        // Then apply search criteria
        const filtered = activeMembers.filter(member => 
            member.name.toLowerCase().includes(value.toLowerCase()) ||
            member.emailAddress.toLowerCase().includes(value.toLowerCase()) ||
            member.membershipId.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredMembers(filtered);
    }, [members, filterActiveMembers]);

    // Filter members by date of birth range
    const handleDateOfBirthFilter = useCallback(() => {
        if (startMonth === null || startDay === null || endMonth === null || endDay === null) {
            message.warning('Please select both start and end dates');
            return;
        }

        const currentYear = dayjs().year();
        
        // Create start and end dates for the current year
        const startDate = dayjs().year(currentYear).month(startMonth - 1).date(startDay);
        const endDate = dayjs().year(currentYear).month(endMonth - 1).date(endDay);

        // Validate that the dates are valid
        if (!startDate.isValid() || !endDate.isValid()) {
            message.error('Invalid date selected');
            return;
        }

        // First filter for active members
        const activeMembers = filterActiveMembers(members);
        
        const filtered = activeMembers.filter(member => {
            // Now check date of birth filtering
            const dob = dayjs(member.dateOfBirth);
            
            // Skip if member's DOB is invalid
            if (!dob.isValid()) {
                return false;
            }
            
            // Extract month and day from the member's DOB
            const memberMonth = dob.month() + 1; // dayjs months are 0-based
            const memberDay = dob.date();
            
            // Create the member's birthday for the current year
            const memberBirthdayThisYear = dayjs().year(currentYear).month(memberMonth - 1).date(memberDay);
            
            // Skip if the birthday date is invalid (e.g., Feb 30)
            if (!memberBirthdayThisYear.isValid()) {
                return false;
            }
            
            // Check if the range crosses year boundary (e.g., Dec 15 to Jan 15)
            if (startDate.isAfter(endDate)) {
                // Range crosses year boundary - check if member's birthday falls in either part
                return memberBirthdayThisYear.isSameOrAfter(startDate) || 
                       memberBirthdayThisYear.isSameOrBefore(endDate);
            } else {
                // Normal range within same year
                return memberBirthdayThisYear.isSameOrAfter(startDate) && 
                       memberBirthdayThisYear.isSameOrBefore(endDate);
            }
        });

        setFilteredMembers(filtered);
        message.success(`Found ${filtered.length} active members with birthdays in the selected range`);
    }, [members, startMonth, startDay, endMonth, endDay, filterActiveMembers]);

    // Clear date filters
    const clearDateFilters = useCallback(() => {
        setStartMonth(null);
        setStartDay(null);
        setEndMonth(null);
        setEndDay(null);
        
        // Show only active members when clearing filters
        const activeMembers = filterActiveMembers(members);
        setFilteredMembers(activeMembers);
    }, [members, filterActiveMembers]);

    // Export selected members to Excel
    const exportToExcel = useCallback(() => {
        if (!gridApi) return;

        const selectedNodes = gridApi.getSelectedRows();
        if (selectedNodes.length === 0) {
            message.warning('Please select at least one member to export');
            return;
        }

        // Create CSV content
        const headers = ['Name', 'Email Address', 'Date of Birth', 'Membership Category', 'Valid From', 'Valid To', 'Status'];
        const csvContent = [
            headers.join(','),
            ...selectedNodes.map(member => [
                `"${member.name}"`,
                `"${member.emailAddress}"`,
                `"${dayjs(member.dateOfBirth).format('DD/MM/YYYY')}"`,
                `"${member.membershipCategory.name}"`,
                `"${dayjs(member.validFrom).format('DD/MM/YYYY')}"`,
                `"${dayjs(member.validTo).format('DD/MM/YYYY')}"`,
                `"${member.isEnabled ? 'Active' : 'Inactive'}"`
            ].join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `member_report_${dayjs().format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.success(`Exported ${selectedNodes.length} members to Excel`);
    }, [gridApi]);

    // Download active members CSV from API
    const downloadActiveMembersCsv = useCallback(async () => {
        try {
            const blob = await withLoading(memberService.exportActiveMembersCsv());
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `active_members_${dayjs().format('YYYY-MM-DD')}.csv`;
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success('Active members CSV downloaded');
        } catch (error) {
            console.error('Error downloading active members CSV:', error);
            message.error('Failed to download active members CSV');
        }
    }, [memberService, withLoading]);

    // Download expired members CSV from API
    const downloadExpiredMembersCsv = useCallback(async () => {
        try {
            const blob = await withLoading(memberService.exportExpiredMembersCsv());
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `expired_members_${dayjs().format('YYYY-MM-DD')}.csv`;
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            message.success('Expired members CSV downloaded');
        } catch (error) {
            console.error('Error downloading expired members CSV:', error);
            message.error('Failed to download expired members CSV');
        }
    }, [memberService, withLoading]);

    // AG Grid column definitions
    const columnDefs: ColDef[] = useMemo(() => [
        {
            headerName: 'Name',
            field: 'name',
            sortable: true,
            filter: 'agTextColumnFilter',
            width: 200,
        },
        {
            headerName: 'Email Address',
            field: 'emailAddress',
            sortable: true,
            filter: 'agTextColumnFilter',
            width: 250,
        },
        {
            headerName: 'Date of Birth',
            field: 'dateOfBirth',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params) => dayjs(params.value).format('DD/MM/YYYY'),
            width: 150,
        },
        {
            headerName: 'Membership Category',
            field: 'membershipCategory.name',
            sortable: true,
            filter: 'agTextColumnFilter',
            width: 200,
        },
        {
            headerName: 'Valid From',
            field: 'validFrom',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params) => dayjs(params.value).format('DD/MM/YYYY'),
            width: 150,
        },
        {
            headerName: 'Valid To',
            field: 'validTo',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params) => dayjs(params.value).format('DD/MM/YYYY'),
            width: 150,
        },
        {
            headerName: 'Status',
            field: 'isEnabled',
            sortable: true,
            filter: 'agTextColumnFilter',
            valueFormatter: (params) => params.value ? 'Active' : 'Inactive',
            width: 100,
        }
    ], []);

    // Row selection configuration
    const rowSelection: RowSelectionOptions = useMemo(() => ({
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: true,
        enableClickSelection: true,
    }), []);

    // Grid ready handler
    const onGridReady = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
    }, []);

    // Handle selection changes
    const onSelectionChanged = useCallback(() => {
        if (gridApi) {
            const selectedNodes = gridApi.getSelectedRows();
            setSelectedCount(selectedNodes.length);
        }
    }, [gridApi]);

    // Handle double-click to view profile
    const onCellDoubleClicked = useCallback((event: any) => {
        const member = event.data as MemberProfile;
        if (member && member.membershipId) {
            navigate(`/profiles/${member.membershipId}`);
        }
    }, [navigate]);

    // Calculate statistics
    const activeMembers = useMemo(() => 
        filteredMembers.filter(member => member.isEnabled).length, 
        [filteredMembers]
    );

    const totalMembers = filteredMembers.length;

    // Month options
    const monthOptions = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' },
    ];

    // Get days in selected month (for more accurate day selection)
    const getDaysInMonth = (month: number) => {
        const currentYear = dayjs().year();
        return dayjs().year(currentYear).month(month - 1).daysInMonth();
    };

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Member Reporting</Title>
            
            {/* Summary Statistics */}
            <Row gutter={16} style={{ marginBottom: '24px' }}>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="Total Members"
                            value={totalMembers}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="Active Members"
                            value={activeMembers}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="Selected Members"
                            value={selectedCount}
                            valueStyle={{ color: '#fa8c16' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Date of Birth Filter Section */}
            <Card title="Filter by Date of Birth" style={{ marginBottom: '24px' }}>
                <Row gutter={16} align="middle">
                    <Col span={6}>
                        <Text strong>Start Date:</Text>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Select
                                placeholder="Select Month"
                                value={startMonth}
                                onChange={(value) => {
                                    setStartMonth(value);
                                    setStartDay(null); // Clear day when month changes
                                }}
                                style={{ width: '100%' }}
                            >
                                {monthOptions.map(month => (
                                    <Option key={month.value} value={month.value}>
                                        {month.label}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                placeholder="Select Day"
                                value={startDay}
                                onChange={setStartDay}
                                style={{ width: '100%' }}
                            >
                                {Array.from({ length: getDaysInMonth(startMonth || 1) }, (_, i) => (
                                    <Option key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </Col>
                    <Col span={6}>
                        <Text strong>End Date:</Text>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Select
                                placeholder="Select Month"
                                value={endMonth}
                                onChange={(value) => {
                                    setEndMonth(value);
                                    setEndDay(null); // Clear day when month changes
                                }}
                                style={{ width: '100%' }}
                            >
                                {monthOptions.map(month => (
                                    <Option key={month.value} value={month.value}>
                                        {month.label}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                placeholder="Select Day"
                                value={endDay}
                                onChange={setEndDay}
                                style={{ width: '100%' }}
                            >
                                {Array.from({ length: getDaysInMonth(endMonth || 1) }, (_, i) => (
                                    <Option key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </Col>
                    <Col span={6}>
                        <Space direction="vertical">
                            <Button
                                type="primary"
                                icon={<FilterOutlined />}
                                onClick={handleDateOfBirthFilter}
                                disabled={startMonth === null || startDay === null || endMonth === null || endDay === null}
                            >
                                Filter by Date of Birth
                            </Button>
                            <Button onClick={clearDateFilters}>
                                Clear Filters
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Search and Actions */}
            <Card style={{ marginBottom: '24px' }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Search
                            placeholder="Search members by name, email, or membership ID"
                            allowClear
                            enterButton={<SearchOutlined />}
                            size="large"
                            style={{ width: 400 }}
                            onSearch={handleSearch}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </Col>
                    <Col>
                        <Space wrap>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={fetchMembers}
                            >
                                Refresh
                            </Button>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={exportToExcel}
                                disabled={selectedCount === 0}
                            >
                                Export Selected ({selectedCount})
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={downloadActiveMembersCsv}
                            >
                                Download Active (CSV)
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={downloadExpiredMembersCsv}
                            >
                                Download Expired (CSV)
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Members Table */}
            <Card>
                <div style={{ marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                    💡 Double-click on any row to view member profile
                </div>
                <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
                    <AgGridReact
                        rowData={filteredMembers}
                        columnDefs={columnDefs}
                        rowSelection={rowSelection}
                        onGridReady={onGridReady}
                        onSelectionChanged={onSelectionChanged}
                        onCellDoubleClicked={onCellDoubleClicked}
                        pagination={true}
                        paginationPageSize={50}
                        suppressRowClickSelection={true}
                        enableRangeSelection={true}
                        animateRows={true}
                        defaultColDef={{
                            resizable: true,
                            sortable: true,
                            filter: true,
                        }}
                    />
                </div>
            </Card>
        </div>
    );
};

export default MemberReporting;
